<template>
  <base-wrapper>
    <div class="space-y-6">
      <create-order-form
        with-origin-office
        :with-origin-office-type="!order.id"
        :with-destination-office="false"
        :with-restock-type="false"
        :with-loan="false"
        :valid="order.valid"
        use-default-office-origin="pusat"
        :origin-office-disabled="order.originOfficeType === 'pusat'"
        with-address-receipt
        use-origin-office-warehouse
        v-model="order"
        @change-origin-office-type="onChangeOriginOfficeType"
      />

      <template v-if="order.originOffice.id">
        <cashback-order-provider
          class="space-y-6"
          :office-type="order.originOfficeType"
          :total-price="totalPrice"
          :area-code="order.area.code"
          :shipment-type="order.shipment_type"
          v-model="cashback"
        >
          <create-order-detail-form
            :cashback="cashback"
            :origin-office="order.originOffice"
            :origin-warehouse="order.originOffice.currentWarehouse"
            :destination-office="order.destinationOffice"
            :buyer-type="order.buyerType"
            :area="order.area"
            :order-id="order.id"
            :stock-free-use-global="false"
            :with-cashback="order.shipment_type === 'pickup'"
            check-published
            with-global-stock
            :columns="[
              'code',
              'name',
              'stock',
              'stock_free',
              'qty',
              'price',
              'total_price',
              'action',
            ]"
            :create-order-attributes="{
              is_using_stock: true,
              order_shipment: order.shipment_type,
              order_type: 'purchase',
              destination_warehouse_id:
                order.destinationOffice.currentWarehouse.id,
              ...(order.custom_shipment_address
                ? {
                    receipt_name: order.shipmentAddress.receipt_name,
                    receipt_phone: order.shipmentAddress.receipt_phone,
                    destination_address: {
                      province_id: order.shipmentAddress.province.id,
                      city_id: order.shipmentAddress.city.id,
                      district_id: order.shipmentAddress.district.id,
                      village_id: order.shipmentAddress.village.id,
                      address: order.shipmentAddress.address,
                    },
                  }
                : {}),
            }"
            :min-spend="minOrderSpend"
            :disabled="order.valid"
            v-model="orderDetails"
            @order-created="onOrderCreated"
            @order-detail-updated="onOrderDetailUpdated"
          />
          <template v-if="canAddPayment">
            <order-promo-list
              :order-details="orderDetailsForPromoList"
              :product-bundles="productBundles"
              :stocks="productBundlesBonusStocks"
              :warehouse-stocks="productBundlesBonusWarehouseStocks"
              @change-selected-bundles="onChangeSelectedBundles"
              @change-choosen-bonuses="onChangeChoosenBonuses"
              @update-remainder-status="status => promoHasRemainder = status"
            />
            <create-order-summary
              :order-details="orderDetails"
              :cashback="cashback"
              :total-payment-amount="totalPayment"
              v-model="order"
            />
            <create-payment-form
              :office-id="order.originOffice.id"
              :office-bank-office-id="order.originOfficeType === 'kantor_pelayanan' ? pusatOfficeId : null"
              :order-id="order.id"
              :payment-id="paymentId"
              :min-amount="totalPrice"
              :filter-reserved="false"
              :filter-payment-method-type="{
                'filter[not-name]': 'Cashback',
              }"
              :filter-payment-method-type-option="filterPaymentMethodTypeOption"
              :paid="cashback"
              :stockist-balance="order.destinationOffice.orderBalanceAvailable"
              :with-validate-button="false"
              :edc-input-bank="false"
              auto-save
              @load-payment-methods-finished="loadingPaymentMethods = false"
              @total-amount-updated="total => totalPayment = total"
            />
          </template>
        </cashback-order-provider>
      </template>

      <div class="flex justify-end gap-2">
        <base-button
          v-if="!order.valid"
          :disabled="!canValidate"
          @click="onValidate"
          >Siap Validasi</base-button
        >
        <base-button color="white" :to="{ name: 'pembelian' }"
          >Kembali</base-button
        >
      </div>
    </div>

    <validate-order-confirmation
      :visible="validateConfirmationVisible"
      :order-id="order.id"
      :validation-data="{
        selected_bundles_id: this.selectedBundes,
        choosen_bonuses: this.choosenBonuses
      }"
      has-transfer-warning
      @close="validateConfirmationVisible = false"
      @confirmed="onConfirmValidate"
    />

    <view-order-modal
      :visible="viewModalVisible"
      :order-id="order.id"
      with-bonus
      :columns="[
        'code',
        'warehouse',
        'office',
        'date',
        'user',
        'area',
        'shipment_type',
        'address',
      ]"
      @close="$router.push({ name: 'pembelian' })"
      @validated="$router.push({ name: 'pembelian' })"
      @deleted="$router.push({ name: 'pembelian' })"
    />

    <loading v-if="loading" />
  </base-wrapper>
</template>

<script>
import CashbackOrderProvider from '@/components/order/cashback-order-provider.vue';
import CreateOrderForm from '@/components/order/create-order-form.vue';
import CreateOrderDetailForm from '@/components/order/create-order-detail-form.vue';
import { mapGetters, mapMutations } from 'vuex';
import { requestMixin } from '@/mixins/request/request';
import ValidateOrderConfirmation from '@/components/order/validate-order-confirmation.vue';
import ViewOrderModal from '@/components/order/view-order-modal.vue';
import CreatePaymentForm from '@/components/payment/create-payment-form.vue';
import OrderPromoList from '@/components/promo/OrderPromoList.vue';
import CreateOrderSummary from '../../../../components/order/create-order-summary.vue';

export default {
  components: {
    CreateOrderForm,
    CreateOrderDetailForm,
    ValidateOrderConfirmation,
    ViewOrderModal,
    CreatePaymentForm,
    CashbackOrderProvider,
    OrderPromoList,
    CreateOrderSummary
  },
  mixins: [requestMixin],
  data() {
    return {
      loadingBuyerType: false,
      loadingDestinationOffice: false,
      loadingOriginOffice: false,
      loadingOrder: false,
      loadingSetup: false,
      loadingPaymentMethods: false,
      loadingPusatOfficeId: true,
      cashback: null,
      minOrderSpend: 0,
      pusatOfficeId: null,
      order: {
        id: null,
        area: {
          id: null,
          code: null,
        },
        buyerType: {
          id: null,
          code: null,
        },
        custom_shipment_address: false,
        date: new Date(),
        destinationOfficeType: 'stockist',
        destinationOffice: {
          id: null,
          code: null,
          hasAddress: false,
          originalCode: null,
          searchCode: null,
          name: null,
          orderBalanceAvailable: null,

          address: null,

          currentWarehouse: {
            id: null,
            code: null,
          },

          area: {
            id: null,
            code: null,
          },
        },
        originOfficeType: null,
        originOffice: {
          id: null,
          code: null,
          originalCode: null,
          searchCode: null,
          name: null,
          currentWarehouse: {
            id: null,
            code: null,
            name: null
          },
          area: {
            id: null,
            code: null,
          },
        },
        shipment_type: 'pickup',
        shipmentAddress: {
          receipt_name: null,
          receipt_phone: null,
          province: {
            id: null,
            name: null,
          },
          city: {
            id: null,
            name: null,
          },
          district: {
            id: null,
            name: null,
          },
          village: {
            id: null,
            name: null,
          },
          address: null,
          area: {
            id: null,
            code: null,
          },
        },
        valid: false,
      },
      orderDetails: [
        {
          id: null,
          productId: null,
          productCode: null,
          originalProductCode: null,
          productName: null,
          stock: null,
          stock_free: null,
          qty: null,
          originalQty: null,
          price: null,
          totalPrice: null,
        },
      ],
      paymentId: null,
      validateConfirmationVisible: false,
      viewModalVisible: false,
      totalPayment: 0,
      productBundles: [],
      productBundlesBonusStocks: {},
      productBundlesBonusWarehouseStocks: {},
      promoHasRemainder: true,
      selectedBundes: [],
      choosenBonuses: [],
    };
  },
  computed: {
    ...mapGetters({
      me: 'auth/getUser',
    }),
    canAddPayment() {
      return this.orderDetails.filter(
        (orderDetail) => orderDetail.id && orderDetail.qty > 0
      ).length && this.paymentId;
    },
    canValidate() {
      if (!this.orderDetails.filter((orderDetail) => orderDetail.qty > 0).length) {
        return false
      }

      if (this.totalPayment < 1) {
        return false
      }

      if (this.productBundles.length > 0 && this.promoHasRemainder) {
        return false
      }

      return this.totalPrice > this.minOrderSpend
    },
    filterPaymentMethodTypeOption() {
      if (this.order.originOfficeType !== 'stockist_center') {
        return null
      }

      return type => type.attributes.name !== 'Saldo Stockist'
    },
    loading() {
      return (
        this.loadingSetup ||
        this.loadingDestinationOffice ||
        this.loadingOriginOffice ||
        this.loadingBuyerType ||
        this.loadingOrder ||
        this.loadingPaymentMethods ||
        this.loadingPusatOfficeId
      );
    },
    totalPrice() {
      return this.orderDetails.reduce(
        (total, orderDetail) => total + orderDetail.totalPrice,
        0
      );
    },
    orderDetailsForPromoList() {
      return this.orderDetails
        .filter(item => item.productId)
        .map(item => ({
          productId: item.productId,
          qty: item.qty,
          productCode: item.productCode 
        }))
    },
  },
  methods: {
    ...mapMutations({
      setCurrentPeriod: 'auth/setCurrentPeriod'
    }),
    async loadBuyerType() {
      this.loadingBuyerType = true;

      const [res, err] = await this.request('/api/v1/buyer-types', {
        params: {
          'filter[code]': 10,
          'fields[buyer-types]': 'code',
        },
      });

      if (!err && res.data.length) {
        const buyerType = res.data[0];

        this.order.buyerType.id = buyerType.id;
        this.order.buyerType.code = buyerType.attributes.code;
      }

      this.loadingBuyerType = false;
    },
    async loadSetup() {
      this.loadingSetup = true;

      const [res, err] = await this.request('/api/v1/setups', {
        params: {
          'filter[setup_key]': 'min_order_spend_for_stockist',
          'fields[setups]': 'setup_value',
        },
      });

      if (!err && res.data.length) {
        this.minOrderSpend = +res.data[0].attributes.setup_value;
      }

      this.loadingSetup = false;
    },
    async loadPusatOfficeId() {
      const [res, err] = await this.request('/api/v1/offices', {
        params: {
          'filter[office_category_id]': 1,
          'filter[code]': 'pusat',
          'fields[offices]': 'code',
        },
      });

      if (!err && res.data.length) {
        this.pusatOfficeId = res.data[0].attributes.office_id;
      }

      this.loadingPusatOfficeId = false
    },
    async loadDestinationOffice() {
      this.loadingDestinationOffice = true;

      const [office, err] = await this.request(
        `/api/v1/offices/${this.me.office_id}`,
        {
          params: {
            include: 'current-warehouse,area',
            'fields[offices]':
              'code,name,full_address,current-warehouse,area,order_balance_available',
            'fields[warehouses]': 'code,name',
            'fields[areas]': 'code,name',
          },
        }
      );

      if (!err) {
        this.order.destinationOffice.id = office.data.id;
        this.order.destinationOffice.code = office.data.attributes.code;
        this.order.destinationOffice.searchCode = office.data.attributes.code;
        this.order.destinationOffice.originalCode = office.data.attributes.code;
        this.order.destinationOffice.name = office.data.attributes.name;
        this.order.destinationOffice.address = office.data.attributes.full_address;
        this.order.destinationOffice.orderBalanceAvailable =
          office.data.attributes.order_balance_available;

        const warehouse = this.getSingleIncluded(
          office,
          office.data.relationships['current-warehouse'].data.id
        );

        this.order.destinationOffice.currentWarehouse.id = warehouse.id;
        this.order.destinationOffice.currentWarehouse.code =
          warehouse.attributes.code;

        const area = this.getSingleIncluded(
          office,
          office.data.relationships.area.data.id
        );

        this.order.destinationOffice.area.id = area.id;
        this.order.destinationOffice.area.code = area.attributes.code;
      }

      this.loadingDestinationOffice = false;
    },
    async loadOrder() {
      this.loadingOrder = true;

      const [order, err] = await this.request(
        `/api/v1/orders/${this.$route.params.id}`,
        {
          params: {
            include:
              'area,buyer-type,destination-warehouse,destination-office,origin-office,origin-warehouse,order-details',
            'fields[orders]':
              'createdAt,receipt_name,receipt_phone,order_shipment,is_valid_for_payment,area,buyer-type,destination-office,destination-warehouse,origin-office,origin-warehouse,order-details,shipment_address,final_price',
            'fields[areas]': 'code',
            'fields[offices]':
              'code,name,address,office_type,area_code,order_balance_available',
            'fields[warehouses]': 'code,name',
            'fields[order-details]':
              'product_id,product_code,product_name,current_stock,product_qty,product_price,total_price,current_stock_free',
          },
        }
      );

      if (!err) {
        this.order.id = order.data.id;

        const area = this.getSingleIncluded(
          order,
          order.data.relationships.area.data.id
        );
        const buyerType = this.getSingleIncluded(
          order,
          order.data.relationships['buyer-type'].data.id
        );
        const destinationOffice = this.getSingleIncluded(
          order,
          order.data.relationships['destination-office'].data.id
        );
        const destinationWarehouse = this.getSingleIncluded(
          order,
          order.data.relationships['destination-warehouse'].data.id
        );
        const originOffice = this.getSingleIncluded(
          order,
          order.data.relationships['origin-office'].data.id
        );
        const originWarehouse = this.getSingleIncluded(
          order,
          order.data.relationships['origin-warehouse'].data.id
        );

        this.order.area.id = area.id;
        this.order.area.code = area.attributes.code;

        this.order.buyerType.id = buyerType.id;
        this.order.buyerType.code = buyerType.attributes.code;

        this.order.custom_shipment_address = false;
        this.order.shipmentAddress.address =
          destinationOffice.attributes.address ||
          order.data.attributes.shipment_address;
        this.order.date = order.data.attributes.createdAt;

        this.order.destinationOffice.id = destinationOffice.id;
        this.order.destinationOffice.code = destinationOffice.attributes.code;
        this.order.destinationOffice.originalCode =
          destinationOffice.attributes.code;
        this.order.destinationOffice.searchCode =
          destinationOffice.attributes.code;
        this.order.destinationOffice.name = destinationOffice.attributes.name;
        this.order.destinationOffice.orderBalanceAvailable =
          destinationOffice.attributes.order_balance_available
        this.order.shipmentAddress.receipt_name =
          order.data.attributes.receipt_name;
        this.order.shipmentAddress.receipt_phone =
          order.data.attributes.receipt_phone;

        this.order.destinationOffice.address =
          destinationOffice.attributes.address;

        this.order.destinationOffice.currentWarehouse.id =
          destinationWarehouse.id;
        this.order.destinationOffice.currentWarehouse.code =
          destinationWarehouse.attributes.code;

        this.order.originOfficeType = originOffice.attributes.office_type;

        this.order.originOffice.id = originOffice.id;
        this.order.originOffice.code = originOffice.attributes.code;
        this.order.originOffice.searchCode = `${originOffice.attributes.code} (${originOffice.attributes.area_code})`;
        this.order.originOffice.originalCode =
          this.order.originOffice.searchCode;
        this.order.originOffice.name = originOffice.attributes.name;

        this.order.originOffice.currentWarehouse.id = originWarehouse.id;
        this.order.originOffice.currentWarehouse.code =
          originWarehouse.attributes.code;
        this.order.originOffice.currentWarehouse.name =
          originWarehouse.attributes.name;

        this.order.originOffice.area.code = originOffice.attributes.area_code;

        this.order.shipment_type = order.data.attributes.order_shipment;

        this.order.valid = order.data.attributes.is_valid_for_payment;

        const orderDetails = this.getIncludedByType(order, 'order-details');

        this.orderDetails = orderDetails.map((orderDetail) => ({
          id: orderDetail.id,
          productId: orderDetail.attributes.product_id,
          productCode: orderDetail.attributes.product_code,
          originalProductCode: orderDetail.attributes.product_code,
          productName: orderDetail.attributes.product_name,
          stock: orderDetail.attributes.current_stock,
          stock_free: orderDetail.attributes.current_stock_free,
          qty: orderDetail.attributes.product_qty || null,
          originalQty: orderDetail.attributes.product_qty || null,
          price: orderDetail.attributes.product_price,
          totalPrice: orderDetail.attributes.total_price,
        }));

        this.orderDetails.push({
          id: null,
          productId: null,
          productCode: null,
          originalProductCode: null,
          productName: null,
          stock: null,
          stock_free: null,
          qty: null,
          originalQty: null,
          price: null,
          totalPrice: null,
        });

        this.loadProductBundles()

        await this.loadPaymentId();
      }

      this.loadingOrder = false;
    },
    async loadPaymentId() {
      const [res, err] = await this.request(
        `/api/v1/orders/${this.order.id}/payments`,
        {
          params: {
            'page[size]': 1,
            'fields[payments]': 'code',
          },
        }
      );

      if (!err && res.data.length) {
        this.paymentId = res.data[0].id;
      }
    },
    async loadProductBundles() {
      const [res, err] = await this.request(`/api/v1/orders/${this.order.id}/product-bundles`, {
        params: {
          sort: 'sort_order'
        }
      })

      if (!err) {
        this.productBundles = res.data
        this.productBundlesBonusStocks = res.meta.free_stocks
        this.productBundlesBonusWarehouseStocks = res.meta.warehouse_free_stocks
      }
    },
    onChangeOriginOfficeType(value) {
      if (value === 'pusat') {
        this.setCurrentPeriod('pusat')
      } else {
        this.setCurrentPeriod('sc')
      }
    },
    onConfirmValidate() {
      this.viewModalVisible = true;
    },
    onOrderCreated(order) {
      this.order.id = order.data.id;

      this.loadPaymentId();

      this.$router.push({
        name: 'pembelian.edit',
        params: {
          id: order.data.id,
        },
      });
    },
    onOrderDetailUpdated() {
      this.loadProductBundles()
    },
    onValidate() {
      this.validateConfirmationVisible = true;
    },
    onChangeSelectedBundles(selectedBundes) {
      const copySelectedBundles = [...selectedBundes]

      copySelectedBundles.sort(([, selectedAtA], [, selectedAtB]) => selectedAtA - selectedAtB)

      this.selectedBundes = copySelectedBundles.map(([bundleId]) => bundleId)
    },
    onChangeChoosenBonuses(choosenBonuses) {
      this.choosenBonuses = {...choosenBonuses}
    }
  },
  async created() {
    this.loadSetup();
    this.setCurrentPeriod('sc')

    await this.loadPusatOfficeId()

    if (this.$route.params.id) {
      await this.loadOrder();

      if (this.canAddPayment) {
        this.loadingPaymentMethods = true
      }
    } else {
      this.loadDestinationOffice();
      this.loadBuyerType();
    }
  },
  beforeRouteLeave(to, from, next) {
    this.setCurrentPeriod('stockist')

    next()
  }
};
</script>
